import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import { useLazyGetLocalizedTextsQuery, useUpdateLocalizedTextsMutation } from 'app/services/system.service'

import { BUTTONS, ERRORS, FORM } from 'locales/locales'
import KendoEditor from 'components/kendo/text-editor/Editor'
import CustomButton from 'components/button/CustomButton'
import { EditorChangeEvent } from '@progress/kendo-react-editor'
import { FixMeLater } from 'types'
import DropdownAlert from 'components/dropdown-alert/DropdownAlert'
import CustomFormInput from 'components/input/custom-form-input/CustomFormInput'
import { NOTIFICATION_LANGUAGES } from 'utils/constants'

const PrivacyTextSettings: React.FC = () => {
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState(false)
  const [language, setLanguage] = React.useState('tr')
  const selectedLang = NOTIFICATION_LANGUAGES.find((lng) => lng.value === language)
  const [privacyTexts, setPrivacyTexts] = useState<FixMeLater>({ [language]: '' })

  const [getPrivacyTexts] = useLazyGetLocalizedTextsQuery()
  const [updatePrivacyTexts] = useUpdateLocalizedTextsMutation()


  useEffect(() => {
    fetchPrivacyTexts()
  }, [])

  const fetchPrivacyTexts = useCallback(async () => {
    try {
      setIsLoading(true)
      const result = await getPrivacyTexts({key: 'privacyTexts'})
      if (result.data?.data && Object.keys(result?.data?.data).length) {
        setPrivacyTexts({ ...result.data.data })
      } else {
        setPrivacyTexts({ [language]: '' })
      }
    } catch (error) {
      console.error('Privacy text yüklenirken hata:', error)
    } finally {
      setIsLoading(false)
    }
  }, [getPrivacyTexts, language])

  const onChange = useCallback((event: EditorChangeEvent) => {
    setPrivacyTexts((prevState: FixMeLater) => ({
      ...prevState,
      [language]: event.html || '',
    }))
  }, [setPrivacyTexts, language])

  const onChangeLanguage = useCallback((value: { value: string; title: string }) => {
    setLanguage(value.value)
    setPrivacyTexts((prevState: FixMeLater) => ({
      ...prevState,
      [value.value]: prevState[value.value] || '',
    }))
  }, [setLanguage, setPrivacyTexts])

  //html taglerini kaldırır
  const stripHtml = useCallback((html: string) => {
    const tmp = document.createElement('DIV')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }, [])

  const onSubmitHandler = useCallback(async () => {
    try {
      setIsLoading(true)
      const system: FixMeLater = await updatePrivacyTexts({
        key: 'privacyTexts',
        values: Object.keys(privacyTexts).reduce((acc: FixMeLater, lng: string) => {
          acc[lng] = stripHtml(privacyTexts[lng])
          return acc
        }, {}),
      })

      if (system.data.success) {
        fetchPrivacyTexts()
      }
    } catch (error) {
      DropdownAlert.ref.show({
        isError: true,
        message: t(ERRORS.SOMETHING_WENT_WRONG_ERROR),
      })
    } finally {
      setIsLoading(false)
    }
  }, [fetchPrivacyTexts, privacyTexts, t])

  const onCancelHandler = useCallback(async () => {
    await fetchPrivacyTexts()
  }, [fetchPrivacyTexts])


  return (
    <ContentCard>
      <ContentCardHeader>{t(FORM.KVKK)}</ContentCardHeader>
      <ContentCardBody>
        <CustomFormInput
          selectedOption={selectedLang ? { ...selectedLang, title: t(selectedLang.title) } : null}
          onChange={onChangeLanguage}
          dropDownData={NOTIFICATION_LANGUAGES.map((lng) => {
            return { ...lng, title: t(lng.title) }
          })}
        />
        <KendoEditor
          dir={language === 'ar' ? 'rtl' : 'ltr'}
          tools={[]}
          content={privacyTexts[language]}
          value={privacyTexts[language]}
          onChange={onChange}
          contentStyle={{ height: 500 }}
        />
        <ActionButtonsContainer>
          <CancelButton text={t(BUTTONS.CANCEL)} onClick={onCancelHandler} disabled={isLoading} />
          <SaveButton text={t(BUTTONS.SAVE)} onClick={onSubmitHandler} disabled={isLoading} />
        </ActionButtonsContainer>
      </ContentCardBody>
    </ContentCard>
  )
}

export default PrivacyTextSettings

const ContentCard = styled.div`
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2), 0 0 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
  border: 1px solid #e3ebf6;
  position: relative;
`

const ContentCardHeader = styled.div`
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 500;
  border-bottom: 1px solid #e3ebf6;
`

const ContentCardBody = styled.div`
  padding: 20px;
`

const ActionButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: white;
  border-top: 1px solid #e3ebf6;
  margin-top: auto;
`

const SaveButton = styled(CustomButton)`
  width: 100px;
  background-color: #1e4ab7;
`

const CancelButton = styled(CustomButton)`
  width: 100px;
  background-color: #6c757d;
`